import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { Grid2 as Grid, Stack } from '@mui/material';
import RenderModuleField from '../../../common/fields/render-fields';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';
// import { useAnnouncements } from '../hooks/useAnnouncements';
import { isAllFieldsValid } from '../../../../utils/team';
// import { Announcement, Author, CreateAnnouncementInput, UpdateAnnouncementInput } from '../types';
// import { getAnnouncementSchema } from '../UI-schema';
// import { getAnnouncementStatus } from '../utils/status';
// import { useAuthors } from '../hooks/useAuthors';
import { useAppConfigOptions } from '../../../../hooks/appConfigs';
import { Announcement } from '../types';
import { getAnnouncementSchema } from '../UI-schema';
import { AnnouncementForm } from '../hooks/useAnnouncements';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onSave: (data: AnnouncementForm) => void;
  announcement: Announcement;
  isLoading: boolean;
}

export default function EditAnnouncementModal({
  open,
  onClose,
  onSave,
  announcement,
  isLoading,
}: Props) {
  const { strings } = useStringsContext();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const valuesFields = useRef<Record<string, any>>(announcement);
  const editedFields = useRef<Record<string, boolean>>({});
  const { appConfigOptions } = useAppConfigOptions();

  const onSubmit = useCallback(async () => {
    function getValue(v: string | null, defaultValue: string | null) {
      if (!v || v === 'all') {
        return defaultValue;
      }
      return v;
    }
    const input = {
      ...valuesFields.current,
      abbrv: getValue(valuesFields.current.abbrv, null),
      type: getValue(valuesFields.current.type, 'WARNING') as 'WARNING' | 'INFO',
      scene: getValue(valuesFields.current.scene, 'myPage'),
    };

    onSave(input);
    onClose?.();
  }, [announcement, onClose]);

  const uiScheme = useMemo(() => {
    if (appConfigOptions) {
      return getAnnouncementSchema(appConfigOptions, announcement);
    }
  }, [appConfigOptions, announcement]);

  const onChange = (fieldKey: string, value: any) => {
    valuesFields.current[fieldKey] = value;
    setIsDirty(true);
  };

  const onValidation = async (
    fieldKey: string,
    value: string | number | boolean,
    validation: any
  ) => {
    const isCurrentFieldValid: boolean = await validation.isValid(value);
    editedFields.current[fieldKey] = isCurrentFieldValid;
    const isFormValid = isAllFieldsValid(editedFields.current);
    setSubmitDisabled(!isFormValid);
  };

  // const status = getAnnouncementStatus(announcement);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={
        announcement.id
          ? strings.announcements.editAnnouncement
          : strings.announcements.createAnnouncement
      }
      maxWidth="md"
    >
      <DialogContent dividers>
        <Grid container spacing={4}>
          {uiScheme?.fields.map((field) => (
            <Grid size={12} key={field.id}>
              <RenderModuleField
                field={field}
                setChange={onChange}
                setValidation={onValidation}
                path="announcement"
                imagePath="announcements"
                fullWidth
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2} sx={{ justifyContent: 'space-between', flex: 1 }}>
          <Stack direction="row" gap={2}>
            <Button color="primary" onClick={onClose}>
              {strings.actions.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={submitDisabled || !isDirty}
              loading={isLoading}
              onClick={() => onSubmit()}
            >
              {strings.actions.save}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
}
